import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useJobBoard } from '../../context/JobBoardContext';
import { useReputation } from '../../context/ReputationContext';
import { useWalletSessionMaybe } from '../../context/WalletSessionContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  CANCEL_REASON_OPTIONS,
  DISPUTE_REASON_OPTIONS,
  ESCROW_MILESTONE_OPTIONS,
  MATCH_WINDOW_MS,
  NO_SHOW_REASON_OPTIONS,
  canTransition,
  scheduleWindowOptions,
} from '../../util/jobLifecycle';
import { getUttRailStatus } from '../../config/settlementRails';
import { isDePaySettlementConfigured, openDePayEscrowPayment } from '../../lib/depayEscrow';
import { requestProgressiveAuth } from '../../util/authIntent';

import ConciergeIntakeBar from './ConciergeIntakeBar';
import IdentitySyncBar from './IdentitySyncBar';
import css from './SimpleAppExperience.module.css';

const authorLabel = {
  buyer: 'You',
  provider: 'Provider',
  system: 'System',
};

const StarPick = ({ value, onChange }) => (
  <div className={css.starRow} role="group" aria-label="Rating">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        className={n <= value ? css.starActive : css.star}
        onClick={() => onChange(n)}
        aria-label={`${n} star${n === 1 ? '' : 's'}`}
      >
        ★
      </button>
    ))}
  </div>
);

/**
 * Match → quotes → schedule → job thread → settle/review/cancel/dispute.
 */
const JobWorkspace = ({ jobId, onDone, onBackToList, activityHref = '/activity' }) => {
  const {
    getJob,
    acceptQuote,
    claimQuote,
    setSchedule,
    transitionJob,
    configureEscrow,
    lockEscrow,
    releaseEscrow,
    refundEscrow,
    addMessage,
    cancelJob,
    openDispute,
    closeMatchWindow,
    flagConcierge,
    syncStatus,
    statusLabel,
    escrowLabel,
  } = useJobBoard();
  const { getProviderStats, hasReviewedJob, submitReview, ensureProfile } = useReputation();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const wallet = useWalletSessionMaybe();

  const job = getJob(jobId);
  const [draftMsg, setDraftMsg] = useState('');
  const [schedulePick, setSchedulePick] = useState('');
  const [panel, setPanel] = useState(null); // cancel | noshow | dispute | review
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [evidence, setEvidence] = useState('');
  const [rating, setRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [nowTick, setNowTick] = useState(Date.now());
  const [escrowAmount, setEscrowAmount] = useState('');
  const [escrowMilestone, setEscrowMilestone] = useState('deposit');
  const [releasePin, setReleasePin] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (!job?.match?.endsAt || job.status !== 'matching') {
      return undefined;
    }
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job?.match?.endsAt, job?.status]);

  useEffect(() => {
    if (job?.providerId) {
      ensureProfile(job.providerId, { name: job.providerName });
    }
    (job?.quotes || []).forEach(q => ensureProfile(q.providerId, { name: q.providerName }));
  }, [job, ensureProfile]);

  useEffect(() => {
    if (!job) {
      return;
    }
    if (job.escrow?.amount) {
      setEscrowAmount(job.escrow.amount);
    } else {
      const q = (job.quotes || []).find(x => x.id === job.acceptedQuoteId);
      if (q?.amount) {
        setEscrowAmount(q.amount);
      }
    }
    if (job.escrow?.milestone) {
      setEscrowMilestone(job.escrow.milestone);
    }
  }, [job]);

  const matchRemainingSec = useMemo(() => {
    if (!job?.match?.endsAt) {
      return 0;
    }
    return Math.max(0, Math.ceil((new Date(job.match.endsAt).getTime() - nowTick) / 1000));
  }, [job, nowTick]);

  if (!job) {
    return (
      <section className={css.panel}>
        <p className={css.cardBlurb}>This request is no longer available.</p>
        <button type="button" className={css.primaryBtn} onClick={onBackToList}>
          My requests
        </button>
      </section>
    );
  }

  const accepted = (job.quotes || []).find(q => q.id === job.acceptedQuoteId);
  const escrow = job.escrow || { status: 'none' };
  const escrowLocked = escrow.status === 'locked';
  const showQuotes = ['matching', 'open', 'quoted'].includes(job.status) || (!!job.quotes?.length && !accepted);
  const showSchedule =
    (job.status === 'accepted' ||
      (accepted && !['in_progress', 'complete', 'settled', 'cancelled', 'disputed'].includes(job.status))) &&
    (!job.scheduleWindow || job.status === 'accepted');
  const showEscrow =
    !['cancelled'].includes(job.status) &&
    (escrow.status !== 'none' ||
      ['accepted', 'scheduled', 'in_progress', 'complete', 'disputed', 'settled'].includes(job.status));
  const reviewed = hasReviewedJob(job.id);
  const terminal = ['settled', 'cancelled'].includes(job.status);

  const sendMessage = () => {
    addMessage(job.id, { author: 'buyer', body: draftMsg });
    setDraftMsg('');
  };

  const confirmSchedule = () => {
    const label =
      scheduleWindowOptions.find(o => o.value === schedulePick)?.label || schedulePick || 'Flexible';
    setSchedule(job.id, label);
  };

  const submitCancel = kind => {
    if (!reason) {
      return;
    }
    cancelJob(job.id, { reason, detail, kind });
    setPanel(null);
    setReason('');
    setDetail('');
  };

  const submitDispute = () => {
    if (!reason) {
      return;
    }
    openDispute(job.id, { reason, detail, evidenceNote: evidence });
    setPanel(null);
    setReason('');
    setDetail('');
    setEvidence('');
  };

  const submitReviewForm = () => {
    if (!job.providerId || !rating) {
      return;
    }
    submitReview({
      jobId: job.id,
      providerId: job.providerId,
      providerName: job.providerName,
      rating,
      comment: reviewComment,
    });
    setPanel(null);
    onDone?.();
  };

  const reasonOptions =
    panel === 'dispute'
      ? DISPUTE_REASON_OPTIONS
      : panel === 'noshow'
        ? NO_SHOW_REASON_OPTIONS
        : CANCEL_REASON_OPTIONS;

  const showConcierge =
    !terminal &&
    !job.acceptedQuoteId &&
    ['matching', 'open', 'quoted'].includes(job.status);

  return (
    <section className={css.panel} data-testid="job-workspace">
      <p className={css.demoBanner}>Live request — match, quotes, job thread</p>
      <h2 className={css.sectionTitle}>{job.title}</h2>
      <p className={css.cardMeta}>
        {statusLabel(job.status)}
        {job.providerName ? ` · ${job.providerName}` : ''}
        {job.scheduleWindow ? ` · ${job.scheduleWindow}` : ''}
        {job.conciergeRequested ? ' · Concierge requested' : ''}
      </p>

      <p className={css.settlementStory} data-testid="settlement-story">
        Settlement path: <strong>cash + handoff PIN</strong> (default — no login).
        {isDePaySettlementConfigured()
          ? ' Wallet escrow is crypto-first (connect wallet when you lock funds).'
          : ' Wallet/DePay optional.'}{' '}
        Clearinghouse: {getUttRailStatus().label}.
      </p>

      <IdentitySyncBar syncStatus={syncStatus} />

      {job.status === 'matching' && job.match ? (
        <div className={css.jobBlock} data-testid="job-match-queue">
          <h3 className={css.jobBlockTitle}>Smart match</h3>
          <p className={css.cardBlurb}>
            Broadcasting to {job.match.eligibleCount || 0} eligible{' '}
            {job.kind === 'proxy' ? 'runners' : 'providers'} ·{' '}
            {job.match.mode === 'first_claim' ? 'First claim wins' : 'Best quote window'} ·{' '}
            {matchRemainingSec}s left
          </p>
          <div className={css.matchBar}>
            <div
              className={css.matchBarFill}
              style={{
                width: `${Math.max(
                  0,
                  Math.min(100, (1 - matchRemainingSec / (MATCH_WINDOW_MS / 1000)) * 100)
                )}%`,
              }}
            />
          </div>
          <div className={css.actionRow} style={{ marginTop: 10 }}>
            <button type="button" className={css.secondaryBtn} onClick={() => closeMatchWindow(job.id)}>
              Close window now
            </button>
          </div>
        </div>
      ) : null}

      {showConcierge ? (
        <ConciergeIntakeBar
          appId={job.appId}
          job={job}
          requested={!!job.conciergeRequested}
          onRequested={({ channel }) => flagConcierge(job.id, { channel })}
        />
      ) : null}

      {showQuotes ? (
        <div className={css.jobBlock}>
          <h3 className={css.jobBlockTitle}>
            {job.quotes?.length ? 'Quotes / claim queue' : 'Waiting for quotes…'}
          </h3>
          <div className={css.cardList}>
            {(job.quotes || []).map(q => {
              const isAccepted = job.acceptedQuoteId === q.id;
              const stats = getProviderStats(q.providerId);
              return (
                <div
                  key={q.id}
                  className={isAccepted ? `${css.quoteCard} ${css.quoteCardActive}` : css.quoteCard}
                  data-testid={`job-quote-${q.id}`}
                >
                  <div className={css.cardTop}>
                    <h3 className={css.cardTitle}>{q.providerName}</h3>
                    <span className={css.badge}>
                      {q.amount} {q.currency}
                    </span>
                  </div>
                  <p className={css.cardMeta}>
                    {q.etaWindow}
                    {stats.avgRating != null
                      ? ` · ★ ${stats.avgRating} (${stats.count})`
                      : ' · New'}
                  </p>
                  {stats.badges?.length ? (
                    <div className={css.storefrontMeta}>
                      {stats.badges.map(b => (
                        <span key={b} className={css.storefrontChip}>
                          {b}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <p className={css.cardBlurb}>{q.note}</p>
                  {!job.acceptedQuoteId ? (
                    <div className={css.actionRow} style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className={css.primaryBtn}
                        onClick={() => acceptQuote(job.id, q.id)}
                      >
                        Accept quote
                      </button>
                      {job.match?.mode === 'first_claim' || job.status === 'matching' ? (
                        <button
                          type="button"
                          className={css.secondaryBtn}
                          onClick={() => claimQuote(job.id, q.id)}
                        >
                          Claim now
                        </button>
                      ) : null}
                    </div>
                  ) : isAccepted ? (
                    <p className={css.demoBanner} style={{ marginTop: 8 }}>
                      Accepted
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {showSchedule ? (
        <div className={css.jobBlock}>
          <h3 className={css.jobBlockTitle}>Schedule</h3>
          {job.scheduleWindow && job.status !== 'accepted' ? (
            <p className={css.cardBlurb}>{job.scheduleWindow}</p>
          ) : (
            <>
              <label className={css.field}>
                <span className={css.fieldLabel}>Preferred window</span>
                <select
                  className={css.input}
                  value={schedulePick}
                  onChange={e => setSchedulePick(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {scheduleWindowOptions.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={css.primaryBtn}
                style={{ marginTop: 10 }}
                disabled={!schedulePick}
                onClick={confirmSchedule}
              >
                Confirm schedule
              </button>
            </>
          )}
        </div>
      ) : null}

      {showEscrow ? (
        <div className={css.jobBlock} data-testid="job-escrow">
          <h3 className={css.jobBlockTitle}>Escrow milestone</h3>
          <p className={css.cardMeta}>
            {escrowLabel(escrow.status)}
            {escrow.amount ? ` · ${escrow.amount} ${escrow.currency || ''}` : ''}
            {escrow.milestone ? ` · ${escrow.milestone}` : ''}
          </p>
          <p className={css.cardBlurb}>
            Demo non-custodial rail — lock optional deposit/full amount, release on confirm or handoff
            PIN. Server never holds keys.
          </p>

          {['none', 'offered'].includes(escrow.status) && !terminal ? (
            <>
              <label className={css.field}>
                <span className={css.fieldLabel}>Milestone</span>
                <select
                  className={css.input}
                  value={escrowMilestone}
                  onChange={e => setEscrowMilestone(e.target.value)}
                >
                  {ESCROW_MILESTONE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={css.field}>
                <span className={css.fieldLabel}>Amount to lock</span>
                <input
                  className={css.input}
                  type="text"
                  value={escrowAmount}
                  onChange={e => setEscrowAmount(e.target.value)}
                  placeholder={accepted?.amount || 'e.g. 5,000'}
                />
              </label>
              <div className={css.actionRow} style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className={css.secondaryBtn}
                  onClick={() =>
                    configureEscrow(job.id, {
                      enabled: true,
                      milestone: escrowMilestone,
                      amount: escrowAmount,
                      currency: accepted?.currency || escrow.currency || 'JMD',
                    })
                  }
                >
                  Save escrow plan
                </button>
                <button
                  type="button"
                  className={css.secondaryBtn}
                  disabled={!escrowAmount && !accepted?.amount}
                  onClick={() => {
                    // Open tier: demo PIN lock — no wallet/email required
                    lockEscrow(job.id, {
                      milestone: escrowMilestone,
                      amount: escrowAmount || accepted?.amount,
                      currency: accepted?.currency || escrow.currency || 'JMD',
                    });
                  }}
                  data-testid="escrow-lock-pin"
                >
                  Lock with PIN (cash path)
                </button>
                <button
                  type="button"
                  className={css.primaryBtn}
                  disabled={!escrowAmount && !accepted?.amount}
                  onClick={() => {
                    const amount = escrowAmount || accepted?.amount;
                    const runWalletLock = async () => {
                      lockEscrow(job.id, {
                        milestone: escrowMilestone,
                        amount,
                        currency: accepted?.currency || escrow.currency || 'JMD',
                      });
                      if (!isDePaySettlementConfigured()) {
                        return;
                      }
                      try {
                        const numeric = String(amount).replace(/[^0-9.]/g, '');
                        if (numeric) {
                          await openDePayEscrowPayment({
                            amount: numeric,
                            onFailed: () => {},
                          });
                        }
                      } catch {
                        // PIN lock already recorded; DePay optional
                      }
                    };
                    if (wallet?.isWalletAuthenticated || wallet?.authMode === 'web3') {
                      runWalletLock();
                      return;
                    }
                    requestProgressiveAuth({
                      history,
                      routes,
                      location,
                      intent: 'settleWallet',
                      onAuthenticated: runWalletLock,
                    });
                  }}
                  data-testid="escrow-lock-wallet"
                >
                  Lock with wallet
                </button>
                {escrow.status === 'offered' ? (
                  <button
                    type="button"
                    className={css.secondaryBtn}
                    onClick={() => configureEscrow(job.id, { enabled: false })}
                  >
                    Skip escrow
                  </button>
                ) : null}
              </div>
            </>
          ) : null}

          {escrowLocked ? (
            <>
              <p className={css.cardBlurb}>
                Handoff PIN (share at completion): <strong>{escrow.pin}</strong>
              </p>
              <label className={css.field}>
                <span className={css.fieldLabel}>Release with PIN</span>
                <input
                  className={css.input}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={releasePin}
                  onChange={e => {
                    setReleasePin(e.target.value);
                    setPinError('');
                  }}
                  placeholder="6-digit PIN"
                />
              </label>
              {pinError ? <p className={css.cardMeta}>{pinError}</p> : null}
              <div className={css.actionRow} style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className={css.primaryBtn}
                  onClick={() => {
                    const ok = releaseEscrow(job.id, { method: 'pin', pin: releasePin });
                    if (!ok) {
                      setPinError('PIN did not match — try again or confirm release.');
                    } else {
                      setPinError('');
                      setReleasePin('');
                    }
                  }}
                >
                  Release via PIN
                </button>
                <button
                  type="button"
                  className={css.secondaryBtn}
                  onClick={() => {
                    releaseEscrow(job.id, { method: 'confirm' });
                    setPinError('');
                  }}
                >
                  Confirm receipt & release
                </button>
                <button
                  type="button"
                  className={css.secondaryBtn}
                  onClick={() => refundEscrow(job.id)}
                >
                  Refund
                </button>
              </div>
            </>
          ) : null}

          {escrow.status === 'released' ? (
            <p className={css.demoBanner}>Released · {escrow.releaseMethod || 'confirm'}</p>
          ) : null}
          {escrow.status === 'refunded' ? (
            <p className={css.demoBanner}>Refunded to payer</p>
          ) : null}

          {escrow.status === 'none' &&
          ['accepted', 'scheduled', 'in_progress', 'complete'].includes(job.status) ? (
            <button
              type="button"
              className={css.secondaryBtn}
              style={{ marginTop: 10 }}
              onClick={() =>
                configureEscrow(job.id, {
                  enabled: true,
                  milestone: escrowMilestone,
                  amount: escrowAmount || accepted?.amount,
                })
              }
            >
              Enable escrow
            </button>
          ) : null}
        </div>
      ) : null}

      {job.cancel ? (
        <div className={css.jobBlock}>
          <h3 className={css.jobBlockTitle}>Cancellation</h3>
          <p className={css.cardBlurb}>
            {job.cancel.kind === 'no_show' ? 'No-show · ' : ''}
            {job.cancel.reason}
            {job.cancel.detail ? ` — ${job.cancel.detail}` : ''}
          </p>
        </div>
      ) : null}

      {job.dispute ? (
        <div className={css.jobBlock}>
          <h3 className={css.jobBlockTitle}>Dispute</h3>
          <p className={css.cardBlurb}>
            {job.dispute.reason}
            {job.dispute.detail ? ` — ${job.dispute.detail}` : ''}
          </p>
          {job.dispute.evidenceNote ? (
            <p className={css.cardMeta}>Evidence: {job.dispute.evidenceNote}</p>
          ) : null}
          {canTransition(job.status, 'settled') ? (
            <button
              type="button"
              className={css.primaryBtn}
              style={{ marginTop: 10 }}
              onClick={() => transitionJob(job.id, 'settled')}
            >
              Resolve & settle
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={css.jobBlock}>
        <h3 className={css.jobBlockTitle}>Job thread</h3>
        <ul className={css.threadList}>
          {(job.messages || []).map(m => (
            <li
              key={m.id}
              className={
                m.author === 'system'
                  ? css.threadSystem
                  : m.author === 'buyer'
                    ? css.threadBuyer
                    : css.threadProvider
              }
            >
              <span className={css.threadAuthor}>{authorLabel[m.author] || m.author}</span>
              <span className={css.threadBody}>{m.body}</span>
            </li>
          ))}
        </ul>
        {!terminal ? (
          <div className={css.threadCompose}>
            <input
              className={css.input}
              type="text"
              placeholder="Message about this job…"
              value={draftMsg}
              onChange={e => setDraftMsg(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              type="button"
              className={css.secondaryBtn}
              disabled={!draftMsg.trim()}
              onClick={sendMessage}
            >
              Send
            </button>
          </div>
        ) : null}
      </div>

      {job.status === 'settled' && job.providerId && !reviewed ? (
        <div className={css.jobBlock} data-testid="job-review">
          <h3 className={css.jobBlockTitle}>Rate {job.providerName || 'provider'}</h3>
          <StarPick value={rating} onChange={setRating} />
          <label className={css.field} style={{ marginTop: 10 }}>
            <span className={css.fieldLabel}>Comment (optional)</span>
            <textarea
              className={css.input}
              rows={2}
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              placeholder="Punctual, quality, communication…"
            />
          </label>
          <button type="button" className={css.primaryBtn} style={{ marginTop: 10 }} onClick={submitReviewForm}>
            Submit review
          </button>
        </div>
      ) : null}

      {job.status === 'settled' && reviewed ? (
        <p className={css.demoBanner}>Thanks — review saved</p>
      ) : null}

      {panel ? (
        <div className={css.jobBlock} data-testid="job-exception-panel">
          <h3 className={css.jobBlockTitle}>
            {panel === 'dispute' ? 'Open dispute' : panel === 'noshow' ? 'Report no-show' : 'Cancel request'}
          </h3>
          <label className={css.field}>
            <span className={css.fieldLabel}>Reason</span>
            <select className={css.input} value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">Choose…</option>
              {reasonOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className={css.field}>
            <span className={css.fieldLabel}>Details</span>
            <textarea
              className={css.input}
              rows={2}
              value={detail}
              onChange={e => setDetail(e.target.value)}
              placeholder="What happened…"
            />
          </label>
          {panel === 'dispute' ? (
            <label className={css.field}>
              <span className={css.fieldLabel}>Evidence note</span>
              <input
                className={css.input}
                type="text"
                value={evidence}
                onChange={e => setEvidence(e.target.value)}
                placeholder="Photos in chat, receipt #, timestamp…"
              />
            </label>
          ) : null}
          <div className={css.actionRow} style={{ marginTop: 10 }}>
            <button type="button" className={css.secondaryBtn} onClick={() => setPanel(null)}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={!reason}
              onClick={() => {
                if (panel === 'dispute') {
                  submitDispute();
                } else {
                  submitCancel(panel === 'noshow' ? 'no_show' : 'cancel');
                }
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : null}

      <div className={css.actionRow} style={{ marginTop: 16, flexWrap: 'wrap' }}>
        {canTransition(job.status, 'in_progress') ? (
          <button
            type="button"
            className={css.primaryBtn}
            onClick={() => transitionJob(job.id, 'in_progress')}
          >
            Start work
          </button>
        ) : null}
        {canTransition(job.status, 'complete') ? (
          <button
            type="button"
            className={css.primaryBtn}
            onClick={() => transitionJob(job.id, 'complete')}
          >
            Mark complete
          </button>
        ) : null}
        {canTransition(job.status, 'settled') && job.status === 'complete' ? (
          <button
            type="button"
            className={css.primaryBtn}
            disabled={escrowLocked}
            title={escrowLocked ? 'Release or refund escrow before settle' : undefined}
            onClick={() => {
              if (escrowLocked) {
                return;
              }
              transitionJob(job.id, 'settled');
              setPanel(null);
            }}
          >
            {escrowLocked ? 'Release escrow to settle' : 'Confirm & settle'}
          </button>
        ) : null}
        {!terminal && !panel ? (
          <>
            {canTransition(job.status, 'disputed') ? (
              <button type="button" className={css.secondaryBtn} onClick={() => setPanel('dispute')}>
                Dispute
              </button>
            ) : null}
            {['scheduled', 'in_progress', 'accepted'].includes(job.status) ? (
              <button type="button" className={css.secondaryBtn} onClick={() => setPanel('noshow')}>
                No-show
              </button>
            ) : null}
            {canTransition(job.status, 'cancelled') ||
            ['matching', 'open', 'quoted', 'accepted', 'scheduled', 'in_progress', 'disputed'].includes(
              job.status
            ) ? (
              <button type="button" className={css.secondaryBtn} onClick={() => setPanel('cancel')}>
                Cancel
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className={css.actionRow} style={{ marginTop: 12 }}>
        <button type="button" className={css.secondaryBtn} onClick={onBackToList}>
          My requests
        </button>
        <Link to={activityHref} className={css.secondaryBtn}>
          Activity hub
        </Link>
      </div>
    </section>
  );
};

export default JobWorkspace;
